import type { ReactNode } from 'react'
import { Toast as BaseToast } from '@base-ui/react/toast'
import { CircleAlert, CircleCheck, X } from 'lucide-react'
import { Button, IconButton } from './Button'
import './toast.css'

/** `neutral` for plain confirmations, `success` when a change was saved, `error` for failures. */
export type ToastTone = 'neutral' | 'success' | 'error'

export type ToastAction = {
  /** A verb that names the result, such as “Undo” or “Try again”. */
  label: string
  /** Runs, then the toast closes. */
  onClick: () => void
}

export type ToastOptions = {
  title?: ReactNode
  /** What happened and, for errors, what to do next. Line breaks in text are kept. */
  description?: ReactNode
  tone?: ToastTone
  action?: ToastAction
  /** Milliseconds before it closes; 0 keeps it open. Defaults to 5000, and 0 for errors so failures stay visible. */
  timeout?: number
  /** Reuse an id to replace a toast that is still showing instead of adding another. */
  id?: string
}

export type ToastHandle = { id: string; dismiss: () => void }

const DEFAULT_TIMEOUT = 5000
const manager = BaseToast.createToastManager()
let count = 0

/** Shows a toast from anywhere, including outside React. A `Toaster` must be mounted once in the app. */
export function toast({ title, description, tone = 'neutral', action, timeout, id = `play-toast-${++count}` }: ToastOptions): ToastHandle {
  manager.add({
    id,
    title,
    description,
    type: tone,
    // High priority makes Base UI announce errors through an assertive alert; others are polite.
    priority: tone === 'error' ? 'high' : 'low',
    timeout: timeout ?? (tone === 'error' ? 0 : DEFAULT_TIMEOUT),
    actionProps: action && {
      children: action.label,
      onClick: () => {
        action.onClick()
        manager.close(id)
      },
    },
  })
  return { id, dismiss: () => manager.close(id) }
}

/** Closes one toast by id, or every toast. */
export function dismissToast(id?: string) {
  manager.close(id)
}

const api = { toast, dismiss: dismissToast }

/** The toast functions as a hook, matching the shadcn `useToast()` call sites. The returned functions are stable. */
export function useToast() {
  return api
}

export type ToasterProps = {
  /** How many toasts show at once; older ones step behind. */
  limit?: number
  /** Portal target, for example an active fullscreen root. Defaults to the document body. */
  container?: HTMLElement | null
}

/** Renders toasts in the bottom corner, in a “Notifications” landmark that F6 moves focus to. Mount it once. */
export function Toaster({ limit = 3, container }: ToasterProps) {
  return (
    <BaseToast.Provider toastManager={manager} limit={limit} timeout={DEFAULT_TIMEOUT}>
      <BaseToast.Portal container={container ?? undefined}>
        <BaseToast.Viewport className="play-theme play-toast-viewport">
          <ToastList />
        </BaseToast.Viewport>
      </BaseToast.Portal>
    </BaseToast.Provider>
  )
}

const icons: Partial<Record<string, typeof CircleCheck>> = { success: CircleCheck, error: CircleAlert }

function ToastList() {
  const { toasts } = BaseToast.useToastManager()
  return toasts.map((item) => {
    const Icon = item.type ? icons[item.type] : undefined
    return (
      <BaseToast.Root key={item.id} toast={item} className="play-toast">
        <BaseToast.Content className="play-toast__content">
          {Icon && <Icon className="play-toast__icon" aria-hidden="true" />}
          <div className="play-toast__text">
            <BaseToast.Title className="play-toast__title" />
            <BaseToast.Description className="play-toast__description" />
            <BaseToast.Action render={<Button variant="secondary" className="play-toast__action" />} />
          </div>
          <BaseToast.Close render={<IconButton label="Dismiss" variant="ghost" className="play-toast__close"><X /></IconButton>} />
        </BaseToast.Content>
      </BaseToast.Root>
    )
  })
}
